import {
  CreateCategoryChampionRequest as CreateCategoryChampionDto,
  EntitlementSlugEnum,
  PatchRequestParams,
  CreateRequestParams as RequestCreationDto,
  RequestResponse as RequestDto,
  RequestTypeResponse as RequestTypeDto,
  RequestTypeCategoryResponse as RequestTypeCategoryDto,
  RequestTypeSettingsResponse as RequestTypeSettingsDto,
  PaginatedResponseRequestTypeCategoryChampionV2 as PaginatedRequestTypeCategoryChampionsDto,
  PaginatedResponseRequestTypeSettingsResponse as PaginatedRequestTypeSettingsDto,
  SystemRequesterResponse,
  RequestTypeSettingDefaultsResponse as RequestTypeSettingDefaultsResponseDto,
} from '@electricjs/core_entity-client';
import { mapKeys, camelCase } from 'lodash';

import { TagTypes, apiSlice } from '@/redux/slices/apiSlice';
import {
  AggregatedRequestTypeSettingsPreview,
  RequestTypeCategoryChampion,
  DeleteGroupSettingsDefaultsArgs,
  DownloadRequestsArgs,
  GetAggregatedRequestTypeSettingsPreviewArgs,
  GetRequestArgs,
  GetRequestTypeArgs,
  GetRequestTypeCategoryByIdArgs,
  GetRequestTypeCategoryChampionArgs,
  getRequestTypeSettingsArgs,
  GetAppSettingsDefaultsArgs,
  ISVSupportRequestPayload,
  ListRequestsArgs,
  ListRequestsResponse,
  ListRequestTypeSettingsResponse,
  Request,
  RequestCreationArgs,
  RequestType,
  RequestTypeCategory,
  RequestTypeCategoryChampionCreationArgs,
  RequestTypeCategoryChampionAssignmentDeletionArgs,
  RequestTypeListArgs,
  RequestTypeSetting,
  RequestTypeSettingDefaults,
  RequestTypeSettingDefaultsResponse,
  SystemRequester,
  UpdateRequestArgs,
  UpdateRequestDueDateArgs,
  UpsertSettingsDefaultsArgs,
} from '@/types/requests';

import {
  invalidatesListAndById,
  providesById,
  providesListAndAllById,
} from './helpers/cacheTagHelpers';
import { ApiListResponseDto } from '@externalTypes/general';
import { fetchAndDownloadFile } from './helpers/fetchAndDownloadFileHelper';

// `id` is the assignment id (not the champion id): v2 deletes by it. Champions
// without an employee are dropped since callers only handle employees.
export const transformCategoryChampions = (
  response: PaginatedRequestTypeCategoryChampionsDto
): RequestTypeCategoryChampion[] =>
  response.results.flatMap(({ id, rank, champion, division_id }) => {
    if (!champion.employee_id) {
      console.warn(`Ignoring category champion ${id}: it has no employee`);
      return [];
    }
    return [
      {
        id,
        employeeId: champion.employee_id,
        rank,
        divisionId: division_id ?? undefined,
      },
    ];
  });

export const transformRequest = (request: RequestDto): Request => {
  return {
    id: request.id,
    assignedTo: request.assigned_to,
    requestTypeId: request.request_type_id,
    organizationId: request.organization_id,
    requesterUserId: request.requester_user_id,
    systemRequesterId: request.system_requester_id,
    status: request.status,
    automationStatus: request.automation_status,
    automationErrorCode: request.automation_error_code,
    data: (() => {
      const mapped = mapKeys(request.data, (_, key) => camelCase(key));
      if (mapped.impact !== null && typeof mapped.impact === 'object') {
        const { impact, ...rest } = mapped;
        return { ...rest, groupChangeImpact: impact };
      }
      return mapped;
    })(),
    requestCollectionId: request.request_collection_id,
    notBefore: request.not_before,
    onBehalfOf: request.on_behalf_of,
    resolvedAt: request.resolved_at,
    resolvedByUserId: request.resolved_by_user_id,
    dueDate: request.due_date,
    createdAt: request.created_at,
  };
};

export const requestApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    //
    // Requests
    //
    createRequest: builder.mutation<Request, RequestCreationArgs>({
      query: ({ organizationId, ...request }) => {
        const body: RequestCreationDto = {
          request_type_id: request.requestTypeId,
          requester_user_id: request.requesterUserId,
          status: request.status,
          data: request.data,
          request_collection_id: request.requestCollectionId,
          not_before: request.notBefore,
          on_behalf_of: request.onBehalfOf,
        };
        return {
          url: `/v1/organizations/${organizationId}/requests`,
          method: 'POST',
          body: body,
        };
      },
      invalidatesTags: (_results, _error, { organizationId, onBehalfOf }) => [
        ...invalidatesListAndById(TagTypes.Request, organizationId),
        {
          type: TagTypes.EmployeeApplicationsAndRequests,
          id: onBehalfOf,
        },
      ],
    }),
    getMyRequests: builder.query<ListRequestsResponse, ListRequestsArgs>({
      query: ({
        organizationId,
        orderBy,
        sortOrder,
        categorySlug,
        requestCollectionId,
        onBehalfOf,
        requestTypeSlug,
        ...args
      }) => ({
        url: `/v1/organizations/${organizationId}/requests/me`,
        params: {
          ...args,
          sort_order: sortOrder,
          order_by: orderBy,
          category_slug: categorySlug,
          request_collection_id: requestCollectionId,
          on_behalf_of: onBehalfOf,
          request_type_slug: requestTypeSlug,
        },
      }),
      transformResponse: (
        response: ApiListResponseDto<RequestDto>
      ): ListRequestsResponse => {
        return {
          results: response.results.map(transformRequest),
          total: response.total ?? 0,
        };
      },
      providesTags: result =>
        providesListAndAllById(TagTypes.Request)(result?.results),
    }),
    downloadMyRequests: builder.query<void, DownloadRequestsArgs>({
      queryFn: async (
        {
          organizationId,
          status,
          categorySlug,
          onBehalfOf,
          requestTypeId,
          language,
        },
        _api,
        _extraOptions,
        baseQuery
      ) => {
        const url = `/v1/organizations/${organizationId}/requests/me/download`;
        return await fetchAndDownloadFile(
          baseQuery as (arg: unknown) => unknown,
          url,
          'my-tasks.csv',
          undefined,
          {
            status,
            category_slug: categorySlug,
            on_behalf_of: onBehalfOf,
            request_type_id: requestTypeId,
            language,
          }
        );
      },
    }),
    downloadAllRequests: builder.query<void, DownloadRequestsArgs>({
      queryFn: async (
        {
          organizationId,
          status,
          categorySlug,
          onBehalfOf,
          requestTypeId,
          language,
        },
        _api,
        _extraOptions,
        baseQuery
      ) => {
        const url = `/v1/organizations/${organizationId}/requests/download`;
        return await fetchAndDownloadFile(
          baseQuery as (arg: unknown) => unknown,
          url,
          'all-tasks.csv',
          undefined,
          {
            status,
            category_slug: categorySlug,
            on_behalf_of: onBehalfOf,
            request_type_id: requestTypeId,
            language,
          }
        );
      },
    }),
    getRequests: builder.query<ListRequestsResponse, ListRequestsArgs>({
      query: ({
        organizationId,
        orderBy,
        sortOrder,
        assignedTo,
        categorySlug,
        onBehalfOf,
        requestCollectionId,
        requestTypeSlug,
        ...args
      }) => ({
        url: `/v1/organizations/${organizationId}/requests`,
        params: {
          ...args,
          sort_order: sortOrder,
          order_by: orderBy,
          assigned_to: assignedTo,
          category_slug: categorySlug,
          on_behalf_of: onBehalfOf,
          request_collection_id: requestCollectionId,
          request_type_slug: requestTypeSlug,
        },
      }),
      transformResponse: (
        response: ApiListResponseDto<RequestDto>
      ): ListRequestsResponse => {
        return {
          results: response.results.map(transformRequest),
          total: response.total ?? 0,
        };
      },
      providesTags: result =>
        providesListAndAllById(TagTypes.Request)(result?.results),
    }),
    getRequestsCount: builder.query<number, ListRequestsArgs>({
      query: ({
        organizationId,
        orderBy,
        sortOrder,
        assignedTo,
        categorySlug,
        onBehalfOf,
        requestCollectionId,
        requestTypeSlug,
        ...args
      }) => ({
        url: `/v1/organizations/${organizationId}/requests`,
        params: {
          ...args,
          limit: 0,
          sort_order: sortOrder,
          order_by: orderBy,
          assigned_to: assignedTo,
          category_slug: categorySlug,
          on_behalf_of: onBehalfOf,
          request_collection_id: requestCollectionId,
          request_type_slug: requestTypeSlug,
        },
      }),
      providesTags: [TagTypes.Request],
      transformResponse: (response: ApiListResponseDto<RequestDto>) => {
        return response.total ?? 0;
      },
    }),
    getRequestById: builder.query<Request, GetRequestArgs>({
      query: ({ organizationId, requestId }) => {
        return {
          url: `/v1/organizations/${organizationId}/requests/${requestId}`,
        };
      },
      transformResponse: (request: RequestDto) => {
        return transformRequest(request);
      },
      providesTags: (result, _error, args) =>
        providesById(TagTypes.Request, result?.id || args.requestId),
    }),
    updateRequestStatus: builder.mutation<void, UpdateRequestArgs>({
      query: ({
        organizationId,
        requestId,
        resolvedAt,
        resolvedByUserId,
        status,
      }) => {
        const transformedPayload: PatchRequestParams = {
          resolved_at: resolvedAt,
          resolved_by_user_id: resolvedByUserId,
          status: status,
        };
        return {
          url: `/v1/organizations/${organizationId}/requests/${requestId}`,
          method: 'PATCH',
          body: transformedPayload,
        };
      },
      invalidatesTags: (_results, _error, args) => [
        ...invalidatesListAndById(TagTypes.Request, args.requestId),
        TagTypes.EmployeeApplicationAccount,
        TagTypes.EmployeeApplicationsAndRequests,
      ],
    }),
    executeRequest: builder.mutation<
      Request,
      { organizationId: string; requestId: string }
    >({
      query: ({ organizationId, requestId }) => ({
        url: `/v1/organizations/${organizationId}/requests/${requestId}/execute`,
        method: 'POST',
        body: {},
      }),
      transformResponse: (request: RequestDto) => transformRequest(request),
      invalidatesTags: (_results, _error, args) => [
        ...invalidatesListAndById(TagTypes.Request, args.requestId),
        TagTypes.EmployeeApplicationAccount,
        TagTypes.EmployeeApplicationsAndRequests,
      ],
    }),
    updateRequestDueDate: builder.mutation<void, UpdateRequestDueDateArgs>({
      query: ({ organizationId, requestId, dueDate }) => {
        // PatchRequestParams doesn't type due_date as nullable, but we need null
        // to explicitly clear the field (undefined is stripped by JSON.stringify)
        const transformedPayload = {
          due_date: dueDate ?? null,
        } as PatchRequestParams;
        return {
          url: `/v1/organizations/${organizationId}/requests/${requestId}`,
          method: 'PATCH',
          body: transformedPayload,
        };
      },
      invalidatesTags: (_results, _error, args) =>
        invalidatesListAndById(TagTypes.Request, args.requestId),
    }),

    //
    // Request Types
    //
    getRequestTypeById: builder.query<RequestType, GetRequestTypeArgs>({
      query: ({ requestTypeId }) => {
        return {
          url: `/v1/request_types/${requestTypeId}`,
        };
      },
      transformResponse: (requestType: RequestTypeDto) => {
        return {
          id: requestType.id,
          name: requestType.name,
          slug: requestType.slug,
          requestTypeCategoryId: requestType.request_type_category_id,
          isInternal: requestType.is_internal,
        };
      },
      providesTags: (_result, _error, args) =>
        providesById(TagTypes.RequestType, args.requestTypeId),
    }),
    listAllRequestTypes: builder.query<RequestType[], RequestTypeListArgs>({
      query: arg => {
        return {
          params: {
            limit: arg?.limit ?? 100,
            offset: arg?.offset ?? 0,
            requestTypeCategoryId: arg?.requestTypeCategoryId ?? undefined,
          },
          url: `/v1/request_types`,
        };
      },
      providesTags: providesListAndAllById(TagTypes.RequestType),
      transformResponse: (response: { results: RequestTypeDto[] }) => {
        return response?.results?.map(rt => {
          return {
            id: rt.id,
            name: rt.name,
            slug: rt.slug,
            requestTypeCategoryId: rt.request_type_category_id,
            isInternal: rt.is_internal,
          };
        });
      },
    }),
    getRequestTypesByRequestTypeCategoryId: builder.query<
      RequestType[],
      string
    >({
      query: requestTypeCategoryId =>
        `/v1/request_types/${requestTypeCategoryId}/request_types?is_internal=false`,
      transformResponse: (requestTypes: { results: RequestTypeDto[] }) => {
        return requestTypes.results.map(rt => ({
          id: rt.id,
          name: rt.name,
          requestTypeCategoryId: rt.request_type_category_id,
          slug: rt.slug,
          isInternal: rt.is_internal,
        }));
      },
    }),
    //
    // Request Type Settings
    //
    getRequestTypeSettingsByRequestTypeId: builder.query<
      RequestTypeSetting[],
      string
    >({
      query: requestTypeId =>
        `/v1/request_type_settings/${requestTypeId}/request_type_settings`,
      transformResponse: (requestTypeSettings: {
        results: RequestTypeSettingsDto[];
      }) => {
        return requestTypeSettings.results.map(rts => ({
          id: rts.id,
          name: rts.name,
          displayName: rts.display_name,
          type: rts.type,
          required: rts.required,
          allowsDefault: rts.allows_default,
          requestTypeId: rts.request_type_id,
          parentRequestTypeSettingId: rts.parent_request_type_setting_id,
          parentSettingValue: rts.parent_setting_value,
          prompt: rts.prompt,
          defaultValue: rts.default_value,
          options: rts.options,
          optionsUrl: rts.options_url,
          orderValue: rts.order_value,
        }));
      },
    }),
    //
    // Request Type Settings
    //
    getRequestTypeSettings: builder.query<
      ListRequestTypeSettingsResponse,
      getRequestTypeSettingsArgs
    >({
      query: ({ applicationKey, limit, offset, requestTypeKey }) => {
        return {
          url: '/v1/request_type_settings',
          params: {
            request_type_key: requestTypeKey,
            application_key: applicationKey,
            limit: limit ?? 100,
            offset: offset ?? 0,
          },
        };
      },
      transformResponse: (response: PaginatedRequestTypeSettingsDto) => {
        return {
          results: response.results.map(rts => ({
            id: rts.id,
            name: rts.name,
            displayName: rts.display_name,
            type: rts.type,
            required: rts.required,
            allowsDefault: rts.allows_default,
            requestTypeId: rts.request_type_id,
            parentRequestTypeSettingId: rts.parent_request_type_setting_id,
            parentSettingValue: rts.parent_setting_value,
            prompt: rts.prompt,
            defaultValue: rts.default_value,
            options: rts.options,
            optionsUrl: rts.options_url,
            orderValue: rts.order_value,
          })),
          total: response.total,
        };
      },
      providesTags: [TagTypes.RequestTypeSettings],
    }),
    //
    // Organization Request Type Category Champions (assignment-based, deleted by assignment id)
    //
    getRequestTypeCategoryChampionsV2: builder.query<
      RequestTypeCategoryChampion[],
      GetRequestTypeCategoryChampionArgs
    >({
      query: ({ organizationId, requestTypeCategoryId }) => ({
        url: `/v2/organizations/${organizationId}/request_type_category_champions`,
        params: { request_type_category_id: requestTypeCategoryId },
      }),
      transformResponse: transformCategoryChampions,
      providesTags: (_result, _error, args) => [
        {
          type: TagTypes.OrganizationRequestTypeCategoryChampion,
          id: args.requestTypeCategoryId,
        },
        {
          type: TagTypes.OrganizationRequestTypeCategoryChampion,
          id: args.organizationId,
        },
      ],
    }),
    createRequestTypeCategoryChampion: builder.mutation<
      void,
      RequestTypeCategoryChampionCreationArgs
    >({
      query: ({
        organizationId,
        requestTypeCategoryId,
        employeeId,
        rank,
        divisionId,
      }) => {
        const body: CreateCategoryChampionDto = {
          employee_id: employeeId,
          request_type_category_id: requestTypeCategoryId,
          rank,
          division_id: divisionId,
        };
        return {
          url: `/v2/organizations/${organizationId}/request_type_category_champions`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: (_result, _error, args) => [
        ...invalidatesListAndById(
          TagTypes.OrganizationRequestTypeCategoryChampion,
          args.requestTypeCategoryId
        ),
        ...invalidatesListAndById(
          TagTypes.RequestTypeCategoryChampion,
          args.organizationId
        ),
        {
          type: TagTypes.OrganizationApplicationChampionsWithEmployees,
          id: args.organizationId,
        },
      ],
    }),
    deleteRequestTypeCategoryChampionById: builder.mutation<
      void,
      RequestTypeCategoryChampionAssignmentDeletionArgs
    >({
      query: ({ organizationId, assignmentId }) => ({
        url: `/v2/organizations/${organizationId}/request_type_category_champions/${assignmentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, args) => [
        ...invalidatesListAndById(
          TagTypes.OrganizationRequestTypeCategoryChampion,
          args.requestTypeCategoryId
        ),
        ...invalidatesListAndById(
          TagTypes.RequestTypeCategoryChampion,
          args.organizationId
        ),
        {
          type: TagTypes.OrganizationApplicationChampionsWithEmployees,
          id: args.organizationId,
        },
      ],
    }),
    sendISVRequest: builder.mutation<void, ISVSupportRequestPayload>({
      query: ({
        organizationId,
        heading,
        message,
        vendorEmail,
        vendorName,
      }) => {
        return {
          url: `/v1/organizations/${organizationId}/add_ons/send_isv_support_request`,
          method: 'POST',
          body: {
            vendor_info: {
              vendor_name: vendorName,
              vendor_email: vendorEmail,
            },
            request_info: {
              heading: heading,
              message: message,
            },
          },
        };
      },
    }),
    listAllSystemRequesters: builder.query<SystemRequester[], void>({
      query: () => {
        return {
          url: `/v1/system_requesters`,
        };
      },
      providesTags: [TagTypes.SystemRequester],
      transformResponse: (response: SystemRequesterResponse[]) => {
        return response?.map(requester => {
          return {
            id: requester.id,
            name: requester.name,
            createdAt: requester.created_at,
            updatedAt: requester.updated_at,
          };
        });
      },
    }),
    getRequestTypeCategoryById: builder.query<
      RequestTypeCategory,
      GetRequestTypeCategoryByIdArgs
    >({
      providesTags: [TagTypes.RequestType],
      query: ({ organizationId, requestTypeCategoryId }) => {
        return {
          url: `/v1/organizations/${organizationId}/request_type_categories/${requestTypeCategoryId}`,
        };
      },
      transformResponse: (response: RequestTypeCategoryDto) => {
        // ChampionTypeCategoryResponse is not compatible with ChampionResponse
        // (missing employeeId and rank), but this is a pre-existing mismatch in
        // the generated client types. Cast to suppress the error.
        return {
          id: response.id,
          name: response.name,
          slug: response.slug,
          description: response.description,
          champions: response.champions,
          serviceProviderId: response.service_provider_id,
          isSupportCenterTile: response.is_support_center_tile,
        } as RequestTypeCategory;
      },
    }),
    assignRequestToEmployee: builder.mutation<
      void,
      { organizationId: string; requestId: string; employeeId: string }
    >({
      query: ({ organizationId, requestId, employeeId }) => {
        return {
          url: `/v1/organizations/${organizationId}/requests/${requestId}/assign`,
          method: 'POST',
          body: {
            employee_id: employeeId,
          },
          invalidatesTags: [TagTypes.Request],
        };
      },
    }),
    getAppSettingDefaults: builder.query<
      RequestTypeSettingDefaultsResponse,
      GetAppSettingsDefaultsArgs
    >({
      providesTags: (_result, _error, { applicationKey, organizationId }) => [
        {
          type: TagTypes.ApplicationProvisioningSettings,
          id: `${organizationId}-${applicationKey}`,
        },
      ],
      query: ({ organizationId, applicationKey }) => ({
        url: `/v1/organization/${organizationId}/application/${applicationKey}/setting-defaults`,
        meta: {
          entitlements: [EntitlementSlugEnum.Integrations],
        },
      }),
      transformResponse: (
        response: RequestTypeSettingDefaultsResponseDto[]
      ) => {
        return response.map(settings => ({
          requestTypeSlug: settings.request_type_slug,
          groupId: settings.group_id,
          settings: settings.settings as RequestTypeSettingDefaults,
        }));
      },
    }),
    getAggregatedRequestTypeSettingsPreview: builder.query<
      AggregatedRequestTypeSettingsPreview,
      GetAggregatedRequestTypeSettingsPreviewArgs
    >({
      providesTags: (
        _result,
        _error,
        { organizationId, applicationKey, requestTypeKey, groupIds }
      ) => [
        {
          type: TagTypes.ApplicationProvisioningSettings,
          id: `${organizationId}-${applicationKey}-${requestTypeKey}-${[...groupIds].sort().join(',')}`,
        },
        // Also tag with the coarser id used by upsertSettingsDefault /
        // deleteGroupSettingsDefaults so saving/deleting settings for this
        // app invalidates this preview too.
        {
          type: TagTypes.ApplicationProvisioningSettings,
          id: `${organizationId}-${applicationKey}`,
        },
      ],
      query: ({ organizationId, applicationKey, requestTypeKey, groupIds }) => {
        // Repeated `group_id` params (not a single comma-joined value) --
        // built manually since RTK Query's default params serialization
        // would comma-join an array value instead of repeating the key.
        const searchParams = new URLSearchParams();
        groupIds.forEach(groupId => searchParams.append('group_id', groupId));
        const queryString = searchParams.toString();
        return {
          url: `/v1/organization/${organizationId}/application/${applicationKey}/setting-defaults/${requestTypeKey}/preview${queryString ? `?${queryString}` : ''}`,
          meta: {
            entitlements: [EntitlementSlugEnum.Integrations],
          },
        };
      },
      transformResponse: (response: RequestTypeSettingDefaultsResponseDto) => ({
        requestTypeSlug: response.request_type_slug,
        groupId: response.group_id,
        settings: response.settings as RequestTypeSettingDefaults,
      }),
    }),
    upsertSettingsDefault: builder.mutation<
      RequestTypeSettingDefaults,
      UpsertSettingsDefaultsArgs
    >({
      invalidatesTags: (
        _result,
        _error,
        { applicationKey, organizationId }
      ) => [
        {
          type: TagTypes.ApplicationProvisioningSettings,
          id: `${organizationId}-${applicationKey}`,
        },
      ],
      query: ({
        applicationKey,
        organizationId,
        requestTypeKey,
        settings,
        groupId,
      }) => ({
        url: `/v1/organization/${organizationId}/application/${applicationKey}/setting-defaults/${requestTypeKey}`,
        method: 'PUT',
        body: {
          settings,
          ...(groupId && { group_id: groupId }),
        },
      }),
    }),
    deleteGroupSettingsDefaults: builder.mutation<
      void,
      DeleteGroupSettingsDefaultsArgs
    >({
      invalidatesTags: (
        _result,
        _error,
        { applicationKey, organizationId }
      ) => [
        {
          type: TagTypes.ApplicationProvisioningSettings,
          id: `${organizationId}-${applicationKey}`,
        },
      ],
      query: ({ organizationId, applicationKey, requestTypeKey, groupId }) => ({
        url: `/v1/organization/${organizationId}/application/${applicationKey}/setting-defaults/${requestTypeKey}?group_id=${groupId}`,
        method: 'DELETE',
      }),
    }),
    completeAllRequestsInCollection: builder.mutation<
      void,
      { organizationId: string; requestCollectionId: string }
    >({
      query: ({ organizationId, requestCollectionId }) => {
        return {
          url: `/v1/organizations/${organizationId}/request_collections/${requestCollectionId}/requests`,
          method: 'PATCH',
        };
      },
      invalidatesTags: (_result, _error, args) => [
        { type: TagTypes.Request, id: 'LIST' },
        { type: TagTypes.ActiveXboardingEmployeeIds, id: args.organizationId },
      ],
    }),
    resolveGroupChange: builder.mutation<
      Request,
      {
        organizationId: string;
        requestId: string;
        appsToGrant: string[];
        appsToRevoke: string[];
      }
    >({
      query: ({ organizationId, requestId, appsToGrant, appsToRevoke }) => ({
        url: `/v1/organizations/${organizationId}/group-change/${requestId}/resolve`,
        method: 'POST',
        body: {
          apps_to_grant: appsToGrant,
          apps_to_revoke: appsToRevoke,
        },
      }),
      transformResponse: (response: RequestDto) => transformRequest(response),
      invalidatesTags: (_result, _error, { organizationId, requestId }) => [
        ...invalidatesListAndById(TagTypes.Request, organizationId),
        { type: TagTypes.Request, id: requestId },
      ],
    }),
    resolveReturnToWork: builder.mutation<
      Request,
      {
        organizationId: string;
        requestId: string;
        reassignmentsToRevert: string[];
        revertEmailForward: boolean;
        clearOutOfOffice: boolean;
      }
    >({
      query: ({
        organizationId,
        requestId,
        reassignmentsToRevert,
        revertEmailForward,
        clearOutOfOffice,
      }) => ({
        url: `/v1/organizations/${organizationId}/return-to-work/${requestId}/resolve`,
        method: 'POST',
        body: {
          reassignments_to_revert: reassignmentsToRevert,
          revert_email_forward: revertEmailForward,
          clear_out_of_office: clearOutOfOffice,
        },
      }),
      transformResponse: (response: RequestDto) => transformRequest(response),
      invalidatesTags: (_result, _error, { organizationId, requestId }) => [
        ...invalidatesListAndById(TagTypes.Request, organizationId),
        { type: TagTypes.Request, id: requestId },
        { type: TagTypes.RequestTypeCategoryChampion, id: organizationId },
      ],
    }),
  }),
});

// export auto-generated hooks
export const {
  useCreateRequestMutation,
  useGetMyRequestsQuery,
  useGetRequestsQuery,
  useGetRequestsCountQuery,
  useGetRequestByIdQuery,
  useListAllRequestTypesQuery,
  useLazyListAllRequestTypesQuery,
  useGetRequestTypeByIdQuery,
  useLazyGetRequestTypeByIdQuery,
  useUpdateRequestStatusMutation,
  useExecuteRequestMutation,
  useUpdateRequestDueDateMutation,
  useGetRequestTypeCategoryByIdQuery,
  useGetRequestTypeCategoryChampionsV2Query,
  useCreateRequestTypeCategoryChampionMutation,
  useDeleteRequestTypeCategoryChampionByIdMutation,
  useSendISVRequestMutation,
  useGetRequestTypesByRequestTypeCategoryIdQuery,
  useLazyGetRequestTypeSettingsByRequestTypeIdQuery,
  useListAllSystemRequestersQuery,
  useLazyDownloadMyRequestsQuery,
  useLazyDownloadAllRequestsQuery,
  useLazyGetRequestsQuery,
  useAssignRequestToEmployeeMutation,
  useGetRequestTypeSettingsQuery,
  useLazyGetRequestTypeSettingsQuery,
  useGetAppSettingDefaultsQuery,
  useLazyGetAppSettingDefaultsQuery,
  useGetAggregatedRequestTypeSettingsPreviewQuery,
  useUpsertSettingsDefaultMutation,
  useDeleteGroupSettingsDefaultsMutation,
  useCompleteAllRequestsInCollectionMutation,
  useResolveGroupChangeMutation,
  useResolveReturnToWorkMutation,
} = requestApiSlice;
